import React, { Component } from 'react'

import AppStore from 'AppStore'
import { Application, Environment, pages } from 'types'
import { logOut } from 'utils/helper'
import { isAdmin, isSuperAdmin } from 'utils/permission'
import Brand from './Brand'
import Icon, { IconName } from './Icon'

import './css/Sidebar.css'

export type SidebarVisualState = 'default' | 'hover' | 'focus' | 'active' | 'disabled' | 'loading' | 'error' | 'success'

interface Props {
  page: pages
  preview?: boolean
  previewState?: SidebarVisualState
}

interface State {
  mobileOpen: boolean
  statusMessage?: string
  visualState: SidebarVisualState
}

interface NavItem {
  active: boolean
  href: string
  icon: IconName
  label: string
}

const environmentLabels: Record<Environment, string> = {
  prod: 'Produzione',
  qty: 'Quality',
  test: 'Test',
}

export default class Sidebar extends Component<Props, State> {
  state: State = {
    mobileOpen: false,
    visualState: this.props.previewState ?? 'default',
  }

  closeButton?: HTMLButtonElement
  drawer?: HTMLElement
  menuButton?: HTMLButtonElement

  componentDidMount() {
    document.addEventListener('keydown', this.handleDocumentKeyDown)
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleDocumentKeyDown)
    document.body.style.overflow = ''
  }

  componentDidUpdate(previousProps: Props) {
    if (previousProps.previewState !== this.props.previewState && this.props.previewState) {
      this.setState({ visualState: this.props.previewState })
    }
  }

  handleDocumentKeyDown = (event: KeyboardEvent) => {
    if (!this.state.mobileOpen || this.props.preview) return

    if (event.key === 'Escape') {
      event.preventDefault()
      this.closeMobileMenu(true)
      return
    }

    if (event.key !== 'Tab' || !this.drawer) return

    const focusableElements = Array.from(
      this.drawer.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex="0"]')
    ).filter((element) => !element.hasAttribute('aria-hidden') && element.tabIndex >= 0)
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  openMobileMenu = () => {
    if (this.props.preview) return
    document.body.style.overflow = 'hidden'
    this.setState({ mobileOpen: true }, () => this.closeButton?.focus())
  }

  closeMobileMenu = (restoreFocus = false) => {
    document.body.style.overflow = ''
    this.setState({ mobileOpen: false }, () => {
      if (restoreFocus) this.menuButton?.focus()
    })
  }

  changeApplication = async () => {
    const nextApplication: Application = AppStore.application === 'ssm' ? 'mmg' : 'ssm'
    await this.runContextChange(
      () => AppStore.changeApplication(nextApplication),
      `Contesto ${nextApplication.toUpperCase()} selezionato.`
    )
  }

  changeEnvironment = async (environment: Environment) => {
    if (environment === AppStore.environment) return
    await this.runContextChange(
      () => AppStore.changeEnvironment(environment),
      `Ambiente ${environmentLabels[environment]} selezionato.`
    )
  }

  handleLogout = async () => {
    if (this.props.preview) return
    await logOut()
  }

  runContextChange = async (action: () => Promise<void>, successMessage: string) => {
    if (this.props.preview) return

    this.setState({ visualState: 'loading', statusMessage: 'Aggiornamento contesto…' })
    try {
      await action()
      this.setState({ visualState: 'success', statusMessage: successMessage }, () => window.location.reload())
    } catch (error) {
      this.setState({
        visualState: 'error',
        statusMessage: 'Non è stato possibile cambiare contesto. Riprova.',
      })
    }
  }

  getPrimaryNavigation = (): NavItem[] => {
    const { page } = this.props
    const navigation: NavItem[] = [
      { active: page === pages.HOME, href: '/', icon: 'home', label: 'Home' },
      {
        active: page === pages.QUESTION_LIST || page === pages.CREATE_QUESTION,
        href: '/questions',
        icon: 'questions',
        label: 'Domande',
      },
      { active: page === pages.QUESTION_REVISION, href: '/revision', icon: 'review', label: 'Revisione' },
      {
        active: page === pages.TOPIC_LIST || page === pages.CREATE_TOPIC,
        href: '/topics',
        icon: 'topics',
        label: 'Materie',
      },
    ]

    if (isSuperAdmin()) {
      navigation.push({
        active: page === pages.SUBTOPIC_LIST || page === pages.CREATE_SUBTOPIC,
        href: '/subtopics',
        icon: 'subtopics',
        label: 'Argomenti',
      })
    }

    navigation.push({
      active: page === pages.PACK_LIST || page === pages.CREATE_PACK,
      href: '/packs',
      icon: 'packs',
      label: 'Pacchetti',
    })

    if (isSuperAdmin()) {
      navigation.push({
        active: page === pages.USER_LIST || page === pages.CREATE_USER,
        href: '/users',
        icon: 'users',
        label: 'Utenti',
      })
    }

    return navigation
  }

  getAdminNavigation = (): NavItem[] => {
    if (!isSuperAdmin()) return []
    const { page } = this.props

    return [
      {
        active: page === pages.REFERRAL_CAMPAIGNS_PAGE || page === pages.CREATE_REFERRAL_CAMPAIGN,
        href: '/referral-campaigns',
        icon: 'referral',
        label: 'Referral Creator',
      },
      {
        active: page === pages.GET_ARTICLES || page === pages.CREATE_ARTICLE,
        href: '/articles',
        icon: 'articles',
        label: 'Articoli',
      },
      { active: page === pages.EXPORT_PAGE, href: '/imports', icon: 'transfer', label: 'Import / Export' },
    ]
  }

  getCurrentPageLabel = () => {
    const item = [...this.getPrimaryNavigation(), ...this.getAdminNavigation()].find((navItem) => navItem.active)
    return item?.label ?? 'Special Quiz'
  }

  getStatusMessage = (visualState: SidebarVisualState) => {
    if (this.state.statusMessage) return this.state.statusMessage
    if (visualState === 'loading') return 'Aggiornamento contesto…'
    if (visualState === 'error') return 'Il cambio contesto non è riuscito.'
    if (visualState === 'success') return 'Contesto aggiornato.'
    return undefined
  }

  renderNavItem = (item: NavItem) => {
    const { preview } = this.props
    const visualState = this.props.previewState ?? this.state.visualState
    const disabled = visualState === 'disabled' || visualState === 'loading'
    const className = `sidebar__nav-link${item.active ? ' sidebar__nav-link--active' : ''}`

    return (
      <a
        aria-current={item.active ? 'page' : undefined}
        aria-disabled={disabled || undefined}
        className={className}
        href={item.href}
        key={item.href}
        onClick={(event) => {
          if (preview || disabled) event.preventDefault()
          if (!preview && !disabled) this.closeMobileMenu()
        }}
        tabIndex={disabled ? -1 : undefined}
      >
        <Icon className="sidebar__nav-icon" name={item.icon} size={20} />
        <span className="sidebar__nav-label">{item.label}</span>
      </a>
    )
  }

  render() {
    const { preview, previewState } = this.props
    const { mobileOpen } = this.state
    const visualState = previewState ?? this.state.visualState
    const statusMessage = this.getStatusMessage(visualState)
    const controlsDisabled = visualState === 'disabled' || visualState === 'loading'
    const primaryNavigation = this.getPrimaryNavigation()
    const adminNavigation = this.getAdminNavigation()
    const appLabel = AppStore.application.toUpperCase()
    const environmentLabel = environmentLabels[AppStore.environment]
    const userName = [AppStore.user?.name, AppStore.user?.surname].filter(Boolean).join(' ') || 'Amministratore'
    const userRole = isSuperAdmin() ? 'Super admin' : isAdmin() ? 'Admin' : 'Autore'
    const asideClassName = ['sidebar', mobileOpen ? 'sidebar--open' : '', preview ? 'sidebar--preview' : '']
      .filter(Boolean)
      .join(' ')

    return (
      <>
        {!preview && (
          <div className="mobile-nav">
            <button
              aria-controls="app-sidebar"
              aria-expanded={mobileOpen}
              className="mobile-nav__trigger"
              onClick={this.openMobileMenu}
              ref={(element) => (this.menuButton = element ?? undefined)}
              type="button"
            >
              <Icon name="menu" size={21} />
              <span>Menu</span>
            </button>
            <span className="mobile-nav__page">{this.getCurrentPageLabel()}</span>
            <span className="mobile-nav__context">{appLabel}</span>
          </div>
        )}

        {!preview && (
          <button
            aria-hidden={!mobileOpen}
            aria-label="Chiudi il menu"
            className={`sidebar-backdrop${mobileOpen ? ' sidebar-backdrop--open' : ''}`}
            onClick={() => this.closeMobileMenu(true)}
            tabIndex={mobileOpen ? 0 : -1}
            type="button"
          />
        )}

        <aside
          aria-label="Navigazione principale"
          className={asideClassName}
          data-preview-state={preview ? visualState : undefined}
          data-state={visualState}
          id={preview ? undefined : 'app-sidebar'}
          ref={(element) => (this.drawer = element ?? undefined)}
        >
          <div className="sidebar__brand-row">
            <a className="sidebar__brand" href="/" onClick={(event) => preview && event.preventDefault()}>
              <Brand subtitle="Gestionale autori" />
            </a>
            {!preview && (
              <button
                aria-label="Chiudi il menu"
                className="sidebar__close"
                onClick={() => this.closeMobileMenu(true)}
                ref={(element) => (this.closeButton = element ?? undefined)}
                type="button"
              >
                <Icon name="close" size={20} />
              </button>
            )}
          </div>

          <div className="sidebar__context-line">
            <span>{appLabel}</span>
            <span aria-hidden="true">·</span>
            <span>{environmentLabel}</span>
          </div>

          <nav className="sidebar__navigation">
            <div className="sidebar__section">
              <span className="sidebar__section-label">Workspace</span>
              <div className="sidebar__nav-list">{primaryNavigation.map(this.renderNavItem)}</div>
            </div>

            {adminNavigation.length > 0 && (
              <div className="sidebar__section">
                <span className="sidebar__section-label">Amministrazione</span>
                <div className="sidebar__nav-list">{adminNavigation.map(this.renderNavItem)}</div>
              </div>
            )}
          </nav>

          <div className="sidebar__footer">
            <div className="sidebar__user">
              <span aria-hidden="true" className="sidebar__user-avatar">
                {userName.charAt(0).toUpperCase()}
              </span>
              <span className="sidebar__user-copy">
                <strong>{userName}</strong>
                <span>{userRole}</span>
              </span>
            </div>

            {isSuperAdmin() && (
              <button
                aria-disabled={controlsDisabled}
                className="sidebar__context-switch"
                data-state={visualState}
                disabled={controlsDisabled}
                onClick={this.changeApplication}
                type="button"
              >
                {visualState === 'loading' && <span aria-hidden="true" className="sidebar__spinner" />}
                <span>
                  {visualState === 'loading' ? 'Cambio contesto…' : `Passa a ${appLabel === 'SSM' ? 'MMG' : 'SSM'}`}
                </span>
              </button>
            )}

            {isSuperAdmin() && process.env.NODE_ENV !== 'production' && (
              <div className="sidebar__environments" aria-label="Ambiente API">
                {(['prod', 'qty', 'test'] as Environment[]).map((environment) => (
                  <button
                    aria-label={`Usa ambiente ${environmentLabels[environment]}`}
                    aria-pressed={AppStore.environment === environment}
                    data-state={visualState}
                    disabled={controlsDisabled}
                    key={environment}
                    onClick={() => this.changeEnvironment(environment)}
                    type="button"
                  >
                    {environment}
                  </button>
                ))}
              </div>
            )}

            <button className="sidebar__logout" onClick={this.handleLogout} type="button">
              <Icon name="logout" size={18} />
              <span>Esci</span>
            </button>

            <div
              aria-live="polite"
              className="sidebar__status"
              data-state={visualState}
              role={visualState === 'error' ? 'alert' : 'status'}
            >
              {statusMessage}
            </div>
          </div>
        </aside>
      </>
    )
  }
}
